import type { ReactNode } from "react";
import { Card, CardContent } from "./beui/SurfaceCard";
import { ArrowLeft } from "lucide-react";
import { ContractHeaderActions } from "./ContractHeaderActions";
import { Tabs, TabsList, TabsTrigger } from "./beui/Tabs";
import { WorkspaceMetadata } from "./WorkspaceMetadata";

type WorkspaceProps<Tab extends string> = {
  header: { title: string; subtitle: string; amount?: number; messageHref: string | null; messageLabel: string; primaryActions?: ReactNode; actions?: ReactNode };
  tabs: { key: Tab; label: string }[];
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
  onBack: () => void;
  backLabel?: string;
  error: string | null;
  summary: ReactNode;
  journey?: ReactNode;
  fullWidth?: boolean;
  children: ReactNode;
};

/** Shared presentation for contracts, Project negotiation and quotations. Callers own
 * stage-specific content and actions; a proposal does not imply a funded contract. */
export function ContractWorkspaceLayout<Tab extends string>({ header, tabs, activeTab, onTabChange, onBack, backLabel = "Back to contracts", error, summary, journey, children, fullWidth = false }: WorkspaceProps<Tab>) {
  return <div className="container-x max-w-5xl py-6 pb-24">
    <button onClick={onBack} className="mb-4 flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-navy"><ArrowLeft className="h-4 w-4" />{backLabel}</button>
    {error && <div role="alert" className="mb-3 text-sm text-brand-red">{error}</div>}
    <section className="card overflow-visible">
      <div className="contract-workspace-heading">
        <div className="contract-workspace-title"><h1 className="truncate text-2xl font-extrabold text-navy" title={header.title}>{header.title}</h1><div className="mt-1"><WorkspaceMetadata label={header.subtitle} amount={header.amount} /></div></div>
        <div className="contract-header-controls"><ContractHeaderActions messageHref={header.messageHref} messageLabel={header.messageLabel} primaryActions={header.primaryActions} actions={header.actions} /></div>
      </div>
      <nav aria-label="Contract sections" className="border-t border-line px-3"><Tabs value={activeTab} variant="underline" onValueChange={value => { const next = tabs.find(tab => tab.key === value); if (next) onTabChange(next.key); }}><TabsList aria-label="Contract sections">{tabs.map(({ key, label }) => <TabsTrigger key={key} value={key}>{label}</TabsTrigger>)}</TabsList></Tabs></nav>
    </section>
    <div className={`mt-5 grid gap-5 ${fullWidth ? "" : "lg:grid-cols-[minmax(0,1fr)_18rem]"}`}>
      <section className={fullWidth ? "contract-work-content" : "card min-w-0 p-5"}>{children}</section>
      {!fullWidth && <aside className="min-w-0 lg:self-stretch"><Card className="h-full gap-0 py-5 shadow-sm"><CardContent className="px-5">{summary}</CardContent></Card></aside>}
    </div>
    {journey}
  </div>;
}
